import classNames from "classnames";
import type { FC, ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { closeModal, renderModal } from "../utils/awaitableModal";

import { KibelaModal } from "./KibelaModal";

interface Props {
  intl: any;

  title: ReactNode;
  content: ReactNode;

  okButtonLabel?: ReactNode;
  cancelButtonLabel?: ReactNode;

  className?: string;
  okButtonClassName?: string;
  cancelButtonClassName?: string;

  onlyOkButton?: boolean;

  onConfirm();
  onCancel();
}

export const ConfirmDialog: FC<Props> = ({
  title,
  content,
  className,
  okButtonClassName,
  cancelButtonClassName,
  okButtonLabel = <FormattedMessage id="shared.verbs.ok" />,
  cancelButtonLabel = <FormattedMessage id="shared.verbs.cancel" />,
  onlyOkButton,
  onConfirm,
  onCancel,
}) => {
  return (
    <KibelaModal
      contentLabel="confirm"
      overlayClassName="modal-overlay"
      className={classNames("modal-container", className)}
      onRequestClose={onCancel}
      isOpen
    >
      <section className="modal">
        <h1 className="modal-title">{title}</h1>
        <div className="modal-message">{content}</div>
        <div className="modal-actionButtons">
          {!onlyOkButton && (
            <button type="button" className={classNames("button", cancelButtonClassName)} onClick={onCancel}>
              {cancelButtonLabel}
            </button>
          )}
          <button type="button" className={classNames("button", okButtonClassName)} onClick={onConfirm}>
            {okButtonLabel}
          </button>
        </div>
      </section>
    </KibelaModal>
  );
};

interface ConfirmAsyncOptions {
  intl: { messages; locale };
  title: ReactNode | ReadonlyArray<ReactNode>;
  content: ReactNode | ReadonlyArray<ReactNode>;

  okButtonLabel?: ReactNode;
  cancelButtonLabel?: ReactNode;
  okButtonClassName?: string;
  cancelButtonClassName?: string;

  closeOnConfirm?: boolean;

  onlyOkButton?: boolean;
}

export async function confirmAsync({
  intl,
  title,
  content,
  okButtonLabel,
  cancelButtonLabel,
  okButtonClassName,
  cancelButtonClassName,
  closeOnConfirm = true,
  onlyOkButton = false,
}: ConfirmAsyncOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const modal = (
      <ConfirmDialog
        intl={intl}
        title={title}
        content={content}
        okButtonLabel={okButtonLabel}
        cancelButtonLabel={cancelButtonLabel}
        okButtonClassName={okButtonClassName}
        cancelButtonClassName={cancelButtonClassName}
        onConfirm={() => {
          resolve(true);
          if (closeOnConfirm) {
            closeModal();
          }
        }}
        onCancel={() => {
          resolve(false);
          closeModal();
        }}
        onlyOkButton={onlyOkButton}
      />
    );
    renderModal(modal);
  });
}
